import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Bot, 
  Send, 
  X, 
  Plus, 
  History,
  ChevronDown,
  Loader2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import authApi from '../../api/client';  // Use auth API which has /api prefix

// Markdown components defined outside the component to avoid re-creation
const markdownComponents = {
  p: ({children}) => <p className="text-sm mb-2 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({children}) => <ul className="text-sm list-disc pl-5 mb-2 space-y-1">{children}</ul>,
  ol: ({children}) => <ol className="text-sm list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
  li: ({children}) => <li className="text-sm pl-1 leading-relaxed">{children}</li>,
  code: ({inline, children}) => 
    inline 
      ? <code className="bg-gray-200 px-1 rounded text-xs">{children}</code>
      : <pre className="bg-gray-800 text-gray-100 p-2 rounded text-xs overflow-x-auto my-2"><code>{children}</code></pre>,
  h1: ({children}) => <h1 className="text-base font-bold mb-1 mt-2">{children}</h1>,
  h2: ({children}) => <h2 className="text-sm font-bold mb-1 mt-2">{children}</h2>,
  h3: ({children}) => <h3 className="text-sm font-semibold mb-1 mt-1">{children}</h3>,
  strong: ({children}) => <strong className="font-semibold">{children}</strong>,
};

const AIAssistantPanel = ({ isOpen, onClose, currentPage }) => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [useRestFallback, setUseRestFallback] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const wsFailCountRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // REST API fallback function
  const sendMessageViaRest = useCallback(async (message) => {
    if (!user?.orgId) return;
    
    setIsSending(true);
    setIsTyping(true);
    
    // Add user message immediately
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    
    try {
      // Use authApi which has /api prefix built in
      const response = await authApi.post(`/orgs/${user.orgId}/ai-assistant/message`, {
        message,
        conversationId,
        context: { currentPage }
      });
      
      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }
      
      if (response.data.assistantMessage) {
        setMessages(prev => [...prev, response.data.assistantMessage]);
      }
      
      // Update usage stats if provided
      if (response.data.usage) {
        setUsageStats(prev => ({
          ...prev,
          daily: {
            ...prev?.daily,
            messagesUsed: response.data.usage.dailyMessagesUsed,
            messagesRemaining: response.data.usage.dailyRemaining
          }
        }));
      }
    } catch (error) {
      console.error('REST API error:', error);
      toast.error('Error', { description: error.message || 'Failed to send message' });
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  }, [user?.orgId, conversationId, currentPage, toast]);

  // WebSocket connection effect
  useEffect(() => {
    if (!isOpen || !user?.id || !token) return;
    
    // If REST fallback is active, skip WebSocket
    if (useRestFallback) {
      setIsConnected(true); // Show as "connected" for REST mode
      return;
    }
    
    const connectWebSocket = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      // Always use the current page's host for WebSocket to ensure correct routing
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      const wsUrl = `${wsProtocol}//${wsHost}/api/ws/assistant/${user.id}?token=${token}`;
      
      console.log('AI Assistant connecting to:', wsUrl);

      // Set a connection timeout - if WS doesn't connect in 5 seconds, switch to REST
      const connectionTimeout = setTimeout(() => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout - switching to REST API');
          wsRef.current?.close();
          setUseRestFallback(true);
          setIsConnected(true);
        }
      }, 5000);

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          clearTimeout(connectionTimeout);
          setIsConnected(true);
          wsFailCountRef.current = 0; // Reset fail count on successful connection
          console.log('AI Assistant WebSocket connected');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
              case 'connected':
                setMessages([{
                  id: 'welcome',
                  role: 'assistant',
                  content: data.message,
                  timestamp: new Date().toISOString()
                }]);
                break;
              
              case 'typing':
                setIsTyping(data.isTyping);
                break;
              
              case 'response':
                setIsTyping(false);
                if (data.data.conversationId) {
                  setConversationId(data.data.conversationId);
                }
                if (data.data.assistantMessage) {
                  setMessages(prev => [...prev, data.data.assistantMessage]);
                }
                // Update usage stats if provided
                if (data.data.usage) {
                  setUsageStats(prev => ({
                    ...prev,
                    daily: {
                      ...prev?.daily,
                      messagesUsed: data.data.usage.dailyMessagesUsed,
                      messagesRemaining: data.data.usage.dailyRemaining
                    }
                  }));
                }
                break;
              
              case 'error':
                setIsTyping(false);
                // Check if it's a rate limit error
                if (data.data?.limits) {
                  setUsageStats(prev => ({
                    ...prev,
                    limitExceeded: true,
                    limitReason: data.message,
                    rateLimitResetIn: data.data.limits.rateLimitResetIn
                  }));
                }
                toast.error('AI Assistant Error', { description: data.message });
                break;
              
              case 'new_conversation':
                setConversationId(data.data.id);
                setMessages([]);
                break;
              
              case 'history':
                setConversations(data.data);
                break;
              
              case 'usage_stats':
                setUsageStats(data.data);
                break;
              
              default:
                console.log('Unknown message type:', data.type);
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        ws.onclose = (event) => {
          setIsConnected(false);
          console.log('AI Assistant WebSocket closed:', event.code, event.reason);
          
          // Track failures
          wsFailCountRef.current += 1;
          
          // After 3 failures, switch to REST fallback
          if (wsFailCountRef.current >= 3) {
            console.log('Switching to REST API fallback after WebSocket failures');
            setUseRestFallback(true);
            setIsConnected(true); // Show as connected for REST mode
            toast.success('AI Assistant', { description: 'Using standard connection mode' });
            return;
          }
          
          // Reconnect after 3 seconds if not intentionally closed
          if (event.code !== 1000 && isOpen && !useRestFallback) {
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, 3000);
          }
        };

        ws.onerror = (error) => {
          console.error('AI Assistant WebSocket error:', error);
          wsFailCountRef.current += 1;
        };
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        wsFailCountRef.current += 1;
        
        // Switch to REST fallback on error
        if (wsFailCountRef.current >= 2) {
          setUseRestFallback(true);
          setIsConnected(true);
        }
      }
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [isOpen, user?.id, token, toast, useRestFallback]);

  const sendMessage = () => {
    if (!inputValue.trim()) return;
    
    // Use REST fallback if WebSocket is not available
    if (useRestFallback || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      sendMessageViaRest(inputValue.trim());
      setInputValue('');
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    
    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: inputValue.trim(),
      conversationId,
      context: {
        currentPage
      }
    }));

    setInputValue('');
  };

  const startNewConversation = () => {
    // Just clear local state — a new conversation will be created lazily
    // when the user actually sends their first message
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
    
    toast.success('New Conversation', { description: 'Started a fresh conversation' });
  };

  const loadHistory = async () => {
    // Always try REST API first for reliability
    try {
      const response = await authApi.get(`/orgs/${user.orgId}/ai-assistant/conversations`);
      setConversations(response.data || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Error loading conversation history:', error);
      
      // Fallback to WebSocket if REST fails and WS is connected
      if (!useRestFallback && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'get_history' }));
        setShowHistory(true);
      } else {
        toast.error('Error', { description: 'Could not load conversation history' });
      }
    }
  };

  const loadConversation = async (conv) => {
    // Load a specific conversation by ID
    if (useRestFallback && conv.id) {
      try {
        const response = await authApi.get(`/orgs/${user.orgId}/ai-assistant/conversations/${conv.id}`);
        if (response.data) {
          setMessages(response.data.messages || []);
          setConversationId(conv.id);
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
        // Fallback to the messages we already have
        setMessages(conv.messages || []);
        setConversationId(conv.id);
      }
    } else {
      setMessages(conv.messages || []);
      setConversationId(conv.id);
    }
    setShowHistory(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  // Check if user has access
  const hasAccess = ['Instructor', 'TenantAdmin', 'SuperAdmin'].includes(user?.role);
  
  if (!hasAccess) {
    return null;
  }

  // Calculate usage display
  const dailyUsed = usageStats?.daily?.messagesUsed || 0;
  const dailyRemaining = usageStats?.daily?.messagesRemaining;
  const dailyLimit = usageStats?.daily?.messagesLimit || 50;

  // Always show as ready since we have REST fallback
  const isReady = true;

  return (
    <div className="fixed right-4 bottom-4 w-96 h-[600px] bg-white rounded-xl shadow-2xl border flex flex-col z-[60] overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">AI Teaching Assistant</h3>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${isReady ? 'bg-green-400' : 'bg-yellow-400 animate-pulse'}`} />
                <span className="text-white/80 text-xs">
                  {isReady ? 'Ready' : 'Connecting...'}
                </span>
              </div>
              {dailyRemaining !== undefined && (
                <span className="text-white/70 text-xs">
                  • {dailyRemaining}/{dailyLimit} msgs left
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={startNewConversation}
            title="New conversation"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={loadHistory}
            title="History"
          >
            <History className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="border-b bg-gray-50 p-3 max-h-48 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-600">Recent Conversations</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => setShowHistory(false)}
            >
              <ChevronDown className="w-3 h-3" />
            </Button>
          </div>
          {conversations.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-2">No previous conversations</p>
          ) : (
            <div className="space-y-1">
              {conversations.map(conv => (
                <button
                  key={conv.id}
                  className="w-full text-left p-2 rounded hover:bg-gray-100 transition-colors"
                  onClick={() => loadConversation(conv)}
                >
                  <p className="text-xs font-medium text-gray-700 truncate">{conv.title}</p>
                  <p className="text-[10px] text-gray-500">
                    {new Date(conv.updatedAt).toLocaleDateString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-gray-100 text-gray-800 rounded-bl-md'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div className="prose prose-sm prose-gray max-w-none">
                    <ReactMarkdown components={markdownComponents}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t bg-gray-50">
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything..."
            className="flex-1 text-sm"
            disabled={isSending}
          />
          <Button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isSending}
            className="bg-purple-600 hover:bg-purple-700"
            size="icon"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <p className="text-[10px] text-gray-400 mt-2 text-center">
          AI responses may not always be accurate. Verify important information.
        </p>
      </div>
    </div>
  );
};

export default AIAssistantPanel;
